import React, {useEffect, useMemo, useState} from "react";
import EntityTypeIcon, {resolveEntityType} from "../searchSelector/EntityTypeIcon";
import "./userSelectionChips.css";

const IMG_BASE = import.meta.env.VITE_IMG_BASE;

const ENTITY_CHIP_ICON_TYPES = new Set(["ASSET", "CONTRACT"]);

export function usesEntityChipIcon(chipEntity) {
    return ENTITY_CHIP_ICON_TYPES.has(resolveEntityType(chipEntity));
}

/** @deprecated count > collapsedVisible 기준으로 전체보기 표시 */
export const EXPAND_THRESHOLD = 6;
export const DEFAULT_COLLAPSED_VISIBLE = 5;

export function getItemLabel(item) {
    return item?.name ?? item?.assetName ?? item?.title ?? "";
}

export function UserChipAvatar({item, chipEntity}) {
    if (usesEntityChipIcon(chipEntity)) {
        return (
            <span className="user-selection-chip-avatar user-selection-chip-avatar--icon" aria-hidden="true">
                <EntityTypeIcon entity={chipEntity} className="user-selection-chip-entity-icon" />
            </span>
        );
    }

    if (item?.profileImage) {
        return (
            <img
                src={`${IMG_BASE}${item.profileImage}`}
                alt=""
                className="user-selection-chip-avatar user-selection-chip-avatar--img"
            />
        );
    }

    const initial = (getItemLabel(item) || "?").slice(0, 1);

    return (
        <span className="user-selection-chip-avatar user-selection-chip-avatar--initial" aria-hidden="true">
            {initial}
        </span>
    );
}

function SelectionChip({item, chipEntity, onRemove, readonly}) {
    return (
        <span className="user-selection-chip">
            <UserChipAvatar item={item} chipEntity={chipEntity} />
            <span className="user-selection-chip-name">{getItemLabel(item)}</span>
            {!readonly && onRemove && (
                <button
                    type="button"
                    className="user-selection-chip-remove"
                    aria-label={`${getItemLabel(item)} 제거`}
                    onClick={() => onRemove(item)}
                >
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path
                            d="M18 6L6 18M6 6l12 12"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                        />
                    </svg>
                </button>
            )}
        </span>
    );
}

export function UserSelectionToolbar({
    data,
    onViewAll,
    countUnit = "명",
    collapsedVisible = DEFAULT_COLLAPSED_VISIBLE,
}) {
    const count = Array.isArray(data) ? data.filter(Boolean).length : 0;
    const canViewAll = count > collapsedVisible;

    if (count === 0) {
        return null;
    }

    return (
        <div className="user-selection-toolbar">
            <span className="user-selection-chips-count">
                총 {count}
                {countUnit}
            </span>
            {canViewAll && (
                <button type="button" className="user-selection-chips-view-all" onClick={onViewAll}>
                    전체보기
                </button>
            )}
        </div>
    );
}

export function UserSelectionListModal({
    open,
    onClose,
    data,
    chipEntity,
    onRemove,
    readonly,
    countUnit = "명",
    title = "선택 목록",
}) {
    const items = useMemo(() => (Array.isArray(data) ? data.filter(Boolean) : []), [data]);

    useEffect(() => {
        if (!open) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const onKeyDown = (e) => {
            if (e.key === "Escape") {
                onClose?.();
            }
        };
        window.addEventListener("keydown", onKeyDown);

        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [open, onClose]);

    if (!open) {
        return null;
    }

    return (
        <div className="user-selection-modal-root">
            <div className="image-view-modal-background" onClick={onClose} aria-hidden />
            <div
                className="user-selection-modal"
                role="dialog"
                aria-modal="true"
                aria-label={title}
            >
                <div className="user-selection-modal-head">
                    <div className="user-selection-modal-title">{title}</div>
                    <div className="user-selection-modal-meta">
                        총 {items.length}
                        {countUnit}
                    </div>
                    <button
                        type="button"
                        className="user-selection-modal-close"
                        onClick={onClose}
                        aria-label="닫기"
                    >
                        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path
                                d="M18 6L6 18M6 6l12 12"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                            />
                        </svg>
                    </button>
                </div>
                <div className="user-selection-modal-list">
                    {items.map((item) => (
                        <SelectionChip
                            key={item.id}
                            item={item}
                            chipEntity={chipEntity}
                            onRemove={onRemove}
                            readonly={readonly}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

function UserSelectionChips({
    data,
    onRemove,
    readonly,
    style,
    countUnit = "명",
    chipEntity,
    showHeader = true,
    collapsedVisible = DEFAULT_COLLAPSED_VISIBLE,
    listModalTitle,
}) {
    const [listModalOpen, setListModalOpen] = useState(false);

    const items = useMemo(() => (Array.isArray(data) ? data.filter(Boolean) : []), [data]);
    const count = items.length;
    const shouldCollapse = count > collapsedVisible;
    const visibleItems = shouldCollapse ? items.slice(0, collapsedVisible) : items;

    if (count === 0) {
        return null;
    }

    return (
        <>
            <div className="user-selection-chips" style={style}>
                {showHeader && (
                    <UserSelectionToolbar
                        data={data}
                        onViewAll={() => setListModalOpen(true)}
                        countUnit={countUnit}
                        collapsedVisible={collapsedVisible}
                    />
                )}

                <div className="user-selection-chips-list">
                    {visibleItems.map((item) => (
                        <SelectionChip
                            key={item.id}
                            item={item}
                            chipEntity={chipEntity}
                            onRemove={onRemove}
                            readonly={readonly}
                        />
                    ))}

                    {shouldCollapse && (
                        <span className="user-selection-chip user-selection-chip--more">
                            +{count - collapsedVisible}
                        </span>
                    )}
                </div>
            </div>

            {showHeader && (
                <UserSelectionListModal
                    open={listModalOpen}
                    onClose={() => setListModalOpen(false)}
                    data={data}
                    chipEntity={chipEntity}
                    onRemove={onRemove}
                    readonly={readonly}
                    countUnit={countUnit}
                    title={listModalTitle}
                />
            )}
        </>
    );
}

export default UserSelectionChips;
